/**
 * Component: Product form
 * ------------------------------------------------------------------------------
 * Highly coupled scripts to control the behaviour of a product form.
 *
 * @namespace productForm
 */
import log from 'salvo-lite/log'

import {formatMoney} from '@shopify/theme-currency'
import cssClasses from '../helpers/cssClasses'
import {on, percentageSaving} from '../helpers/utils'

class ProductForm {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._settings = {
            addToCart: '[js-product-form="addToCart"]',
            comparePrice: '[js-product-form="comparePrice"]',
            masterSelector: '[js-product-form="masterSelector"]',
            optionSelector: '[js-product-form="optionSelector"]',
            variantSelector: '[js-product-form="variantSelector"]',
            defaultVariant: '[js-product-defaultVariant]',
            price: '[js-product-form="priceWrapper"]',
            priceWrapper: '[js-product-form="priceWrapper"]',
            quantitySelector: '[js-quantity-selector="container"]',
            backInStockBtn: '[js-product-form="bis-btn"]',
            backInStockForm: '[js-product-form="bis-form"]',
            backInStockFormInputs: '[js-product-form="bis-form-inputs"]',
            backInStockEmail: '[js-product-form="bis-email"]',
            backInStockSubmit: '[js-product-form="bis-submit"]',
            backInStockNotification: '[js-product-form="bis-notification"]',
            increment: '[data-quantity-selector-increment]',
            decrement: '[data-quantity-selector-decrement]',
            input: '[data-quantity-selector-input]',
            InstallationCost: '[js-product-form="installation-cost"]',
            InstallationParts: '[js-product-form="installation-parts"]',
            InstallationBundle: '[js-product-form="installation-bundle"]',
            InstallationProduct: '[js-product-form="installation-product"]',
            productTitle: '[js-product-form="title"]',
            sku: '[js-product-form="sku"]',
            catalogue: '.product-form__catalogue',
            ...JSON.parse(elem.getAttribute('data-carousel') || '{}')
        }

        /**
         * DOM node selectors.
         */
        this._nodeSelectors = {
            addToCart: this._elem.querySelector(this._settings.addToCart),
            comparePrice: this._elem.querySelector(this._settings.comparePrice),
            masterSelector: this._elem.querySelector(this._settings.masterSelector),
            optionSelector: [...this._elem.querySelectorAll(this._settings.optionSelector)],
            variantSelector: [...this._elem.querySelectorAll(this._settings.variantSelector)],
            defaultVariant: this._elem.querySelector(this._settings.defaultVariant),
            price: this._elem.querySelector(this._settings.price),
            priceWrapper: this._elem.querySelector(this._settings.priceWrapper),
            quantitySelector: this._elem.querySelector(this._settings.quantitySelector),
            backInStock: {
                btn: this._elem.querySelector(this._settings.backInStockBtn),
                form: this._elem.querySelector(this._settings.backInStockForm),
                formInputs: this._elem.querySelector(this._settings.backInStockFormInputs),
                email: this._elem.querySelector(this._settings.backInStockEmail),
                submit: this._elem.querySelector(this._settings.backInStockSubmit),
                notification: this._elem.querySelector(this._settings.backInStockNotification)
            },
            increment: this._elem.querySelector(this._settings.increment),
            decrement: this._elem.querySelector(this._settings.decrement),
            input: this._elem.querySelector(this._settings.input),
            InstallationCost: this._elem.querySelector(this._settings.InstallationCost),
            InstallationParts: this._elem.querySelector(this._settings.InstallationParts),
            InstallationBundle: this._elem.querySelector(this._settings.InstallationBundle),
            InstallationProduct: this._elem.querySelector(this._settings.InstallationProduct),
            productTitle: document.querySelector(this._settings.productTitle),
            sku: document.querySelector(this._settings.sku),
            catalogue: this._elem.querySelector(this._settings.catalogue)
        }

        /**
         * Global properties.
         */
        this._productDataSelector = 'product'
        this._productData = JSON.parse(document.getElementById(this._productDataSelector).innerHTML)
        this._product = Object.freeze(window.ProductJSON)
        this._defaultProductTitle = this._product.title
        this._currentVariant = {}
        this._inputValue = 1
        this._variantInverntoryQuantity = ''
        this._variantSelected = ''
        this._variants = ''

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
   * Initialise component.
   */
    onInit () {
        if (this._nodeSelectors.catalogue) {
            return false
        }

        if (this._nodeSelectors.input) {
            this._inputValue = this._nodeSelectors.input.value
        }

        if (this._isProductOutOfStock()) {
            this._setBisClickEvents()
        }

        // if (this._nodeSelectors.optionSelector.length > 0) {
        //     this._disableUnavailableOptions()
        //     this._preselectDefaultVariant()
        //     this._skuUpdate()
        //     this._onPriceChange(this._getVariantFromOptions())
        //     this._updateMasterSelect(this._getVariantFromOptions())
        // }

        // if (this._currentVariant) {
        //     this._currentVariant = this._getVariantFromOptions()
        //     window.Frame.EventBus.emit('Variant:change', this._currentVariant)
        // }

        // this._setDefaultState()
        // this._setListeners()
        // this._setOptionSelectorEvents()
        // this._increaseClick()
        // this._descreaseClick()
        // this._oneInStockDisableIncrease()

        // if (this._variants.sku.includes('fitted')) {
        //     this._isVariantFitted()
        // }
    }

    /**
     * Get variant ID
     */
    _variantsInfomation () {
        this._variants = this._getVariantFromOptions()
        if (this._variants) {
            this._variantSelected = this._variants.id
        }
    }

    /**
     * Set current variant quantity
     */
    _setCurrentVariantInventory () {
        this._variantsInfomation()

        window.ProductVariantMetfields.variantData.forEach((variant) => {
            if (variant.id === this._variantSelected) {
                this._variantInverntoryQuantity = variant.inventory_quantity
            }
        })
    }

    /**
     * On init if product only has one in stock disable incremeant button
     */
    _oneInStockDisableIncrease () {
        this._setCurrentVariantInventory()

        if (this._variantInverntoryQuantity <= this._inputValue) {
            this._nodeSelectors.increment.setAttribute('disabled', true)
        }
    }

    /**
     * Onclick of decrease button
     */
    _descreaseClick () {
        on('click', this._nodeSelectors.decrease, (event) => this._enableIncrease())
    }

    /**
     * Checks variant quantity against input quantity and when less than max enables increase button
     */
    _enableIncrease () {
        this._setCurrentVariantInventory()

        let inputValue = this._nodeSelectors.input.value

        if (this._variantInverntoryQuantity > inputValue && !this._variants.sku.includes('fitted')) {
            this._nodeSelectors.increment.removeAttribute('disabled')
        }
    }

    /**
     * Onclick of increase button
     */
    _increaseClick () {
        on('click', this._nodeSelectors.increment, (event) => this._disableIncrease())
    }

    /**
     * Checks variant quantity against input quantity and when max reached then disable increase button
     */
    _disableIncrease () {
        this._setCurrentVariantInventory()
        const inputValue = this._nodeSelectors.input.value

        if (this._variantInverntoryQuantity <= inputValue) {
            this._nodeSelectors.increment.setAttribute('disabled', true)
        }
    }

    /**
     * Set current variant quantity
     */
    _variantChanged (response) {
        this._titleChange()
        this._skuUpdate()
        this._updateAddToCartState(response)
        this._enableIncrease()
        this._oneInStockDisableIncrease()
        this._nodeSelectors.input.setAttribute('value', '1')
        this._isVariantFitted()

        if (this._variantInverntoryQuantity > 1 && !this._variants.sku.includes('fitted')) {
            this._nodeSelectors.increment.removeAttribute('disabled')
        }
    }

    /**
     * When variant changes checks if variant has fitted in the SKU and will add line items for fitted products, or remove if product isn't a fitted product.
     */
    _isVariantFitted () {
        this._variantsInfomation()
        let installationCost = ''
        let installationParts = ''

        window.ProductVariantMetfields.variantData.forEach((variant) => {
            if (variant.id === this._variantSelected) {
                installationCost = variant.installation_cost
                installationParts = variant.installation_parts
            }
        })

        let ProductlineItemInputs = `
        <input id="installationBundle" name="properties[_installation_bundle]" type="hidden" value="true" js-product-form="installation-bundle">
        <input id="installationCost" name="properties[_installation_cost]" type="hidden" value="${installationCost}" js-product-form="installation-cost">
        <input id="installationParts" name="properties[_installation_parts]" type="hidden" value="${installationParts}" js-product-form="installation-parts">
        `

        if (this._variants) {
            if (this._variants.sku.includes('fitted')) {
                this._nodeSelectors.increment.setAttribute('disabled', true)
                this._nodeSelectors.InstallationProduct.innerHTML = ProductlineItemInputs
            } else if (document.getElementById('installationBundle') && document.getElementById('installationCost') && document.getElementById('installationParts')) {
                document.getElementById('installationBundle').remove()
                document.getElementById('installationCost').remove()
                document.getElementById('installationParts').remove()
                this._nodeSelectors.quantitySelector.style.display = 'flex'
            }
        }
    }

    /**
     * Updates title on frontend when variant is changed unless product is a Lifestyle product
     */
    _titleChange () {
        if (this._isLifestyleProduct()) return false

        const updatedVariants = this._getVariantFromOptions()

        if (this._nodeSelectors.defaultVariant !== null) {
            if (updatedVariants.title === this._nodeSelectors.defaultVariant.dataset.value) {
                this._nodeSelectors.productTitle.innerHTML = this._defaultProductTitle
            } else {
                this._nodeSelectors.productTitle.innerHTML = `${this._defaultProductTitle} & ${updatedVariants.title}`
            }
        }
    }

    /**
     * Updates sku on frontend when variant is changed
     */
    _skuUpdate () {
        const updatedVariants = this._getVariantFromOptions()
        let updatedSkuWithoutFitted = ''

        if (updatedVariants) {
            if (updatedVariants.sku && updatedVariants.sku.includes('fitted')) {
                updatedSkuWithoutFitted = updatedVariants.sku.replace('-fitted', '')
                this._nodeSelectors.sku.innerText = window.theme.strings.sku.concat(updatedSkuWithoutFitted)
            } else if (updatedVariants.sku) {
                this._nodeSelectors.sku.innerText = window.theme.strings.sku.concat(updatedVariants.sku)
            }
        }
    }

    /**
     * Set change events on option selectors.
     */
    _setOptionSelectorEvents () {
        [...this._nodeSelectors.optionSelector].forEach((element) => {
            const type = element.getAttribute('type')

            if (type === 'radio' || type === 'checkbox') {
                on('click', element, () => this._onSelectChange())
                return
            }
            on('change', element, () => this._onSelectChange())
        })
    }

    /**
     * Set default state.
     */
    _setDefaultState () {
        if (window.theme.features.productSwatches) {
            this._productSwatches({
                data: this._productData
            }).init()
        }
    }

    /**
     * Set listeners.
     */
    _setListeners () {
        window.Frame.EventBus.listen('Variant:change', (response) => {
            this._variantChanged(response)
        })
        window.Frame.EventBus.listen('Variant:priceChange', (response) => {
            this._onPriceChange(response)
        })

        window.Frame.EventBus.listen('ProductSwatches:available', (response) => this._handleSwatchAvailability(response))
        window.Frame.EventBus.listen('ProductSwatches:optionSelected', (response) => this._handleOptionChange(response))
        window.Frame.EventBus.listen('ProductSwatches:reset', () => this._resetForm())
        window.Frame.EventBus.listen('ProductSwatches:soldOut', () => this._updateAddToCartState(window.theme.strings.soldOut))
    }

    /**
     * Set click events.
     */
    _setBisClickEvents () {
        on('click', this._nodeSelectors.backInStock.btn, () => this._initiateBackInStockForm())
        on('click', this._nodeSelectors.backInStock.submit, (event) => this._submitBackInStock(event))
    }

    /**
     * Initiate the back in stock form.
     */
    _initiateBackInStockForm () {
        this._nodeSelectors.backInStock.btn.classList.add(cssClasses.hidden)
        this._nodeSelectors.backInStock.form.classList.remove(cssClasses.hidden)
    }

    /**
     * Check to see whether product is out of stock
     */
    _isProductOutOfStock () {
        if (!this._nodeSelectors.backInStock.btn) {
            return false
        }

        return true
    }

    /**
     * Submit the back in stock form.
     * @param {object} event the event of the element that is clicked.
     */
    _submitBackInStock (event) {
        event.preventDefault()
        const email = this._nodeSelectors.backInStock.email.value
        const productId = event.currentTarget.dataset.productId
        const variantId = event.currentTarget.dataset.variantId
        // eslint-disable-next-line
        BIS.create(email, variantId, productId).then(this._backInStockCallback());
    }

    /**
     * The response from the BIS app.
     */
    _backInStockCallback () {
        const callback = (data) => {
            let msg = ''
            if (data.status === 'OK') {
                msg = data.message
                this._nodeSelectors.backInStock.notification.classList.add(cssClasses.active, cssClasses.successful)
                this._nodeSelectors.backInStock.notification.classList.remove(cssClasses.erroneous)
                this._nodeSelectors.backInStock.formInputs.classList.add(cssClasses.hidden)
            } else {
                Object.values(data.errors).forEach((error) => {
                    msg += error.join()
                })
                this._nodeSelectors.backInStock.notification.classList.add(cssClasses.active, cssClasses.erroneous)
            }
            this._nodeSelectors.backInStock.notification.innerHTML = msg
        }

        return callback
    }

    /**
     * Get the currently selected options from product form.
     * Works with all form input elements.
     * @return {array} options - Values of currently selected variants.
     */
    _getCurrentOptions () {
        const currentOptions = [];

        [...this._nodeSelectors.optionSelector].forEach((element) => {
            const type = element.getAttribute('type')
            const currentOption = {}

            if (type === 'radio' || type === 'checkbox') {
                if (element.checked) {
                    currentOption.value = element.value
                    currentOption.index = element.dataset.index

                    currentOptions.push(currentOption)
                }
            } else {
                currentOption.value = element.value
                currentOption.index = element.dataset.index

                currentOptions.push(currentOption)
            }
        })

        return currentOptions
    }

    /**
     * Find variant based on selected values.
     * @return {object || undefined} found - Variant object from product.variants.
     */
    _getVariantFromOptions () {
        const selectedValues = this._getCurrentOptions()
        const variants = this._product.variants
        let found = false

        variants.forEach((variant) => {
            let satisfied = true

            selectedValues.forEach((option) => {
                if (satisfied) {
                    satisfied = (option.value === variant[option.index])
                }
            })

            if (satisfied) {
                found = variant
            }
        })

        return found || null
    }

    /**
     * Event handler for when a variant price changes.
     */
    _onPriceChange (response) {
        this._nodeSelectors.price.innerHTML = this._formatVariantPrice(response)
    }

    /**
     * Format variant price.
     * @param {Object} data - Product data.
     * @returns {String}
     */
    _formatVariantPrice (data) {
        if (data.price === data.compare_at_price) {
            return `
            <span class="price price--large">${formatMoney(data.price, window.theme.moneyFormat)}</span>
            <span class="product-form__vat weight--medium">${window.theme.strings.standard.vatIncluded}</span>
        `
        }

        return `
        <span class="price price--large">${formatMoney(data.price, window.theme.moneyFormat)}</span>
        <s class="price price--compare-at">${formatMoney(data.compare_at_price, window.theme.moneyFormat)}</s>
        <span class="price price--percentage-off">(${percentageSaving(data.price, data.compare_at_price)}${window.theme.strings.percentageOff})</span>
        <span class="product-form__vat weight--medium is-expanded">${window.theme.strings.standard.vatIncluded}</span>
        `
    }

    /**
     * Event handler for when a variant input changes.
     */
    _onSelectChange () {
        const variant = this._getVariantFromOptions()

        window.Frame.EventBus.emit('Variant:change', variant)

        if (!variant) {
            return
        }

        this._updateMasterSelect(variant)
        this._updatePrice(variant)

        this._currentVariant = variant

        if (this._settings.enableHistoryState) {
            this._updateHistoryState(variant)
        }
    }

    /**
     * Updates the DOM state of the add to cart button.
     * @param {boolean} enabled - Decides whether cart is enabled or disabled.
     * @param {string} text - Updates the text notification content of the cart.
     */
    _updateAddToCartState (variant) {
        if (variant) {
            this._enableAddToCart()
            this._nodeSelectors.priceWrapper.classList.remove(cssClasses.hidden)
            this._nodeSelectors.sku.classList.remove(cssClasses.hidden)
        } else {
            this._disableAddToCart()
            this._nodeSelectors.priceWrapper.classList.add(cssClasses.hidden)
            this._nodeSelectors.sku.classList.add(cssClasses.hidden)
            this._nodeSelectors.addToCart.innerText = window.theme.strings.unavailable
            return
        }

        if (variant.available) {
            this._enableAddToCart()
            this._nodeSelectors.addToCart.innerText = window.theme.strings.addToCart
        } else {
            this._disableAddToCart()
            this._nodeSelectors.addToCart.innerText = window.theme.strings.soldOut
        }
    }

    /**
     * Trigger event when variant price changes.
     * @param {object} variant - Currently selected variant.
     */
    _updatePrice (variant) {
        window.Frame.EventBus.emit('Variant:priceChange', variant)
    }

    /**
     * Update hidden master select of variant change.
     * @param {object} variant - Currently selected variant.
     */
    _updateMasterSelect (variant) {
        this._nodeSelectors.masterSelector.value = variant.id
    }

    /**
     * Update history state for product deeplinking.
     * @param {object} variant - Currently selected variant.
     */
    _updateHistoryState (variant) {
        if (!window.history.replaceState || !variant) {
            return
        }

        const newurl = `
        ${window.location.protocol}//${window.location.host}${window.location.pathname}?variant=${variant.id}
        `
        window.history.replaceState({path: newurl}, '', newurl)
    }

    /**
     * Reset form and assign prompt text to add to cart button.
     */
    _resetForm () {
        this._disableAddToCart()

        if (this._productData.available) {
            this._nodeSelectors.addToCart.innerText = window.theme.strings.prompt
        }
    }

    /**
     * Add disable state to add to cart button.
     */
    _disableAddToCart () {
        this._nodeSelectors.addToCart.setAttribute('disabled', true)
        this._nodeSelectors.addToCart.classList.add(cssClasses.disabled)
    }

    /**
     * Set add to cart button to active.
     */
    _enableAddToCart () {
        this._nodeSelectors.addToCart.removeAttribute('disabled')
        this._nodeSelectors.addToCart.classList.remove(cssClasses.disabled)
    }

    /**
     * Pre-select default variant.
     */
    _preselectDefaultVariant () {
        if (this._nodeSelectors.defaultVariant) this._nodeSelectors.defaultVariant.setAttribute('selected', true)
    }

    /**
     * Disable unavailable options.
     */
    _disableUnavailableOptions () {
        this._nodeSelectors.variantSelector.forEach((item) => {
            if (this._findOutOfStockVariants(item)) {
                item.setAttribute('disabled', true)
            }
        })
    }

    /**
     * Check if options is not avaialble.
     * @param {object} variantOption - Variant option.
     */
    _findOutOfStockVariants (variantOption) {
        let variantAvailability = false

        this._product.variants.forEach((variant) => {
            if (variant.title === variantOption.dataset.value && !variant.available) {
                variantAvailability = true
            }
        })

        return variantAvailability
    }

    /**
     * Returns true if product has LifeStyle tag
     */
    _isLifestyleProduct () {
        const productTags = this._productData.tags
        for (let i = 0; i < this._productData.tags.length; i++) {
            if (productTags[i] === 'Accessories_T1: Lifestyle Collection') return true
        }
        return false
    }

    /**
     * Expose public interface.
     */
    // return Object.freeze({
    //     init,
    //     getVariantFromOptions,
    // });
}

export default ProductForm



// WEBPACK FOOTER //
// ./src/resources/scripts/components/ProductForm.js